/**
 * Fraud rings — accounts tied together by a shared device or a card passed
 * between them, graded on their issuer fraud declines (the 22 Oct ring
 * review's method without its IP links, over all time), and one ring's accounts
 * (?ring=<ring ID>). Laid out like the Rings tab of the 28 Oct fraud review.
 * Data: GET /analytics/tt/rings and /analytics/tt/ring.
 */
import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft } from "lucide-react";
import { Button, Segmented } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import { bucketLabel, count, dateOnly, dateTime, money, pct } from "../ttFormat";
import {
  B,
  ChartBox,
  Fact,
  Foot,
  INK,
  Lede,
  LoadState,
  OUTCOME_COLORS,
  PartyPill,
  Pill,
  RankBars,
  ReviewCard,
  ReviewGrid,
  TONE,
  Table,
  axisProps,
} from "../ttKit";

const GRADE_TONES = { Confirmed: "red", Probable: "amber", Review: "slate", "Not a stolen-card ring": "green" };
const LINK_WORDS = { device: "shared devices", card: "cards passed between them" };
const linkWords = (kinds) => Object.keys(LINK_WORDS).filter((k) => kinds.includes(k)).map((k) => LINK_WORDS[k]);
const TOP_RINGS = 10;

export default function RingsPanel({ fresh, openAccount }) {
  const [params, setParams] = useSearchParams();
  const ring = params.get("ring");

  const openRing = useCallback(
    (id) => {
      const next = new URLSearchParams(params);
      if (id) next.set("ring", id);
      else next.delete("ring");
      setParams(next);
    },
    [params, setParams]
  );

  if (ring) return <RingDetail ring={ring} fresh={fresh} openAccount={openAccount} onBack={() => openRing(null)} />;
  return <RingsList fresh={fresh} openRing={openRing} />;
}

function RingsList({ fresh, openRing }) {
  const [scope, setScope] = useState("high");
  const state = useTTQuery("rings", { scope, fresh });
  const gc = state.data?.grade_counts || {};
  const high = (gc.Confirmed || 0) + (gc.Probable || 0);
  const all = Object.values(gc).reduce((a, b) => a + b, 0);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <Segmented
          size="sm"
          value={scope}
          onChange={setScope}
          options={[
            { value: "high", label: `High fraud (${count(high)})` },
            { value: "review", label: `With review (${count(high + (gc.Review || 0))})` },
            { value: "all", label: `All rings (${count(all)})` },
          ]}
        />
      </div>
      <LoadState state={state} height={360}>
        {(d) => <RingsBody d={d} openRing={openRing} />}
      </LoadState>
    </>
  );
}

function RingsBody({ d, openRing }) {
  const links = linkWords(d.link_kinds);
  const linkText = links.length > 1 ? `${links.slice(0, -1).join(", ")} and ${links[links.length - 1]}` : links[0];
  const daily = d.daily.map((x) => ({ label: bucketLabel(x.day, "day"), ...x }));
  const active = daily.some((x) => x.attempts > 0);

  return (
    <>
      <Lede>
        <>
          <B>{count(d.accounts)} accounts</B> work in <B>{count(d.rings.length)} linked groups</B>
          {linkText ? `, tied together by ${linkText}` : ""}.
        </>
        <>
          Accounts linked to no one had <B>{pct(d.baseline.decline_rate)}</B> of their attempts refused as fraud (
          {count(d.baseline.accounts)} accounts, {count(d.baseline.attempts)} attempts). Each group is graded against
          that rate.
        </>
      </Lede>

      <ReviewGrid>
        <ReviewCard eyebrow="Linked groups" title="Groups by money refused" desc="Value of attempts issuers declined as fraud, by group.">
          <RankBars
            rows={d.rings.slice(0, TOP_RINGS)}
            label="id"
            value="fraud_declines_usd"
            format={money}
            barColor={TONE.red.fg}
            empty="No rings in this view."
          />
        </ReviewCard>

        <ReviewCard
          eyebrow="Linked groups"
          title="The last ten days"
          desc="Attempts by these groups' accounts, and how many issuers refused as fraud."
          tag={active ? "Still active" : null}
          tagTone="amber"
        >
          <ChartBox height={260}>
            <ResponsiveContainer>
              <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.line} />
                <XAxis dataKey="label" {...axisProps} minTickGap={8} />
                <YAxis {...axisProps} allowDecimals={false} width={36} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="attempts" name="Attempts" fill={INK.bar} />
                <Bar dataKey="fraud_declines" name="Fraud declines" fill={OUTCOME_COLORS.fraud_declines} />
                <Bar dataKey="completed" name="Completed" fill={OUTCOME_COLORS.completed} />
              </BarChart>
            </ResponsiveContainer>
          </ChartBox>
        </ReviewCard>
      </ReviewGrid>

      <ReviewCard
        eyebrow="Linked groups"
        title={`The ${count(d.rings.length)} groups`}
        desc="Each group is named after its earliest account. Click a row to see its accounts."
      >
        <Table
          emptyState="No rings in this view."
          columns={RING_COLUMNS}
          rows={d.rings.map((r) => ({ key: r.id, ...r, onClick: () => openRing(r.id) }))}
        />
        <Foot>
          Accounts are linked only by a shared device or a shared card; a shared IP alone never links them. A card links
          two accounts when an issuer had already refused it, it was passed on within 24 hours, or both typed the same
          cardholder name. Confirmed and Probable are the high-fraud grades. As of{" "}
          {dateTime(d.as_of)}.
        </Foot>
      </ReviewCard>
    </>
  );
}

const RING_COLUMNS = [
  { key: "id", label: "Group", render: (r) => <span style={{ color: TONE.blue.text }}>{r.id}</span> },
  { key: "grade", label: "Grade", render: (r) => <Pill tone={GRADE_TONES[r.grade] || "slate"}>{r.grade}</Pill> },
  { key: "accounts", label: "Accounts", numeric: true, render: (r) => count(r.accounts) },
  { key: "blacklisted", label: "Blacklisted", numeric: true, render: (r) => count(r.blacklisted) },
  { key: "attempts", label: "Attempts", numeric: true, render: (r) => count(r.attempts) },
  { key: "fraud_declines", label: "Declines", numeric: true, render: (r) => count(r.fraud_declines) },
  { key: "fraud_declines_usd", label: "Refused", numeric: true, render: (r) => money(r.fraud_declines_usd) },
  { key: "decline_rate", label: "Rate", numeric: true, render: (r) => pct(r.decline_rate, 0) },
  { key: "active", label: "Active", render: (r) => `${dateOnly(r.first)} → ${dateOnly(r.last)}` },
];

function RingDetail({ ring, fresh, openAccount, onBack }) {
  const state = useTTQuery("ring", { ring, fresh });
  return (
    <>
      <Button size="sm" variant="ghost" iconLeft={<ArrowLeft size={14} />} onClick={onBack} style={{ marginBottom: 14 }}>
        All rings
      </Button>
      <LoadState state={state} height={240}>
        {(d) => {
          const r = d.ring;
          return (
            <>
              <ReviewCard
                eyebrow="Fraud ring"
                title={r.id}
                desc={`${count(r.accounts)} accounts, active ${dateOnly(r.first)} → ${dateOnly(r.last)}. Linked by ${
                  linkWords(r.link_kinds).join(", ") || "—"
                }.`}
                tag={r.grade}
                tagTone={GRADE_TONES[r.grade] || "slate"}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
                  <Fact label="Attempts">
                    {count(r.attempts)} · {money(r.attempts_usd)}
                  </Fact>
                  <Fact label="Refused by issuers">
                    {count(r.fraud_declines)} · {money(r.fraud_declines_usd)} ({pct(r.decline_rate, 0)})
                  </Fact>
                  <Fact label="Completed">
                    {count(r.completed)} · {money(r.completed_usd)}
                  </Fact>
                  <Fact label="Blacklisted accounts">{count(r.blacklisted)}</Fact>
                  <Fact label="Shared devices">{count(r.shared_devices)}</Fact>
                  <Fact label="Shared cards (refused)">
                    {count(r.shared_cards)} ({count(r.shared_cards_fraud)})
                  </Fact>
                  <Fact label="Cards passed on after a fraud decline">{count(r.cards_passed_after_fd)}</Fact>
                </div>
              </ReviewCard>

              <ReviewCard eyebrow="Accounts" title="Accounts in this ring" desc="Most refused first. Click a row to open the account.">
                <Table
                  emptyState="No accounts."
                  columns={MEMBER_COLUMNS}
                  rows={d.members.map((m) => ({ key: m.email, ...m, onClick: () => openAccount(m.email) }))}
                />
              </ReviewCard>
            </>
          );
        }}
      </LoadState>
    </>
  );
}

const MEMBER_COLUMNS = [
  { key: "email", label: "Account", render: (m) => <span style={{ color: TONE.blue.text }}>{m.email}</span> },
  {
    key: "party_status",
    label: "Status",
    render: (m) => (m.party_status && m.party_status !== "NONE" ? <PartyPill status={m.party_status} /> : ""),
  },
  { key: "cards", label: "Cards", numeric: true, render: (m) => count(m.cards) },
  { key: "attempts", label: "Attempts", numeric: true, render: (m) => count(m.attempts) },
  { key: "fraud_declines", label: "Declines", numeric: true, render: (m) => count(m.fraud_declines) },
  { key: "fraud_decline_usd", label: "Refused", numeric: true, render: (m) => money(m.fraud_decline_usd) },
  { key: "completed_usd", label: "Completed", numeric: true, render: (m) => money(m.completed_usd) },
];
